/**
 * 管理端 REST API 端点集中配置。
 *
 * 所有 REST 路径字面量（含动态段模板）必须集中在此导出为 `ApiPaths`，
 * `*.api.ts` 一律从本文件导入，禁止在 `http.get/post/...` 调用中内联字符串。
 * 对齐 `openspec/context/coding/frontend-api-endpoints.md` §1/§3。
 *
 * 注意：Transport BASE_URL 已含 `/api` 前缀，此处路径不含 `/api`。
 */
export const ApiPaths = {
  auth: {
    /** 获取扫码登录二维码（POST /auth/wechat-login/qrcode） */
    wechatLoginQrcode: "/auth/wechat-login/qrcode",
    /** 轮询扫码登录会话状态（GET /auth/wechat-login/sessions/:token） */
    wechatLoginSession: (token: string) =>
      `/auth/wechat-login/sessions/${token}`,
    /** 扫码登录回调（GET /auth/wechat-login/callback） */
    wechatLoginCallback: "/auth/wechat-login/callback",
    /** fake 模式可选员工列表（GET /auth/wechat-login/mock-staffs） */
    wechatLoginMockStaffs: "/auth/wechat-login/mock-staffs",
    /** fake 模式员工点选登录（POST /auth/wechat-login/mock-login） */
    wechatLoginMockLogin: "/auth/wechat-login/mock-login",
    /** 当前会话信息（GET /auth/session） */
    session: "/auth/session",
    /** 退出登录（POST /auth/logout） */
    logout: "/auth/logout",
    /** 当前用户权限（GET /auth/permissions） */
    permissions: "/auth/permissions",
    /** 微信扫码绑定回调（GET /auth/wechat-bind/callback） */
    wechatBindCallback: "/auth/wechat-bind/callback",
  },
  staffs: {
    /** 员工列表（GET，含筛选与分页） */
    list: "/staffs",
    /** 新建员工（POST） */
    create: "/staffs",
    /** 员工详情（GET） */
    detail: (id: string) => `/staffs/${id}`,
    /** 编辑员工基本信息（PATCH） */
    update: (id: string) => `/staffs/${id}`,
    /** 停用员工（POST） */
    suspend: (id: string) => `/staffs/${id}/suspend`,
    /** 启用员工（POST） */
    activate: (id: string) => `/staffs/${id}/activate`,
    /** 保存校区授权（PUT） */
    grants: (id: string) => `/staffs/${id}/grants`,
    /** 全量替换员工每周可排课时段（staff-availability:SC-06）。 */
    availability: (id: string) => `/staffs/${id}/availability`,
    /** 全量替换员工临时不可排课日期（staff-availability:SC-09）。 */
    availabilityExceptions: (id: string) =>
      `/staffs/${id}/availability-exceptions`,
    /** 生成微信绑定二维码（POST） */
    wechatBindQrcode: (id: string) => `/staffs/${id}/wechat-bind-qrcode`,
    /** 轮询微信绑定状态（GET） */
    wechatBindSession: (id: string, token: string) =>
      `/staffs/${id}/wechat-bind/sessions/${token}`,
    /** 解绑微信（DELETE） */
    wechatUnbind: (id: string) => `/staffs/${id}/wechat-bind`,
  },
  roles: {
    /** 角色列表（GET） */
    list: "/roles",
    /** 新建角色（POST） */
    create: "/roles",
    /** 可配置菜单键目录（GET，后端静态路由） */
    menuKeys: "/roles/menu-keys",
    /** 角色详情含权限矩阵（GET） */
    detail: (id: string) => `/roles/${id}`,
    /** 编辑角色名称/权限/状态（PATCH） */
    update: (id: string) => `/roles/${id}`,
    /** 角色成员分页列表（GET） */
    members: (id: string) => `/roles/${id}/members`,
  },
  systemSettings: {
    /** 读取全部业务配置参数 */
    list: "/system-settings",
    /** 读取安全默认值 */
    defaults: "/system-settings/defaults",
    /** 更新业务配置参数（PATCH） */
    update: "/system-settings",
  },
  organizations: {
    /** 读取当前机构信息 */
    current: "/organizations/current",
    /** 更新机构默认配置（PATCH） */
    update: "/organizations/current",
  },
  campuses: {
    /** 校区列表（GET，含筛选与分页） */
    list: "/campuses",
    /** 校区详情（GET） */
    detail: (id: string) => `/campuses/${id}`,
    /** 新建校区（POST） */
    create: "/campuses",
    /** 编辑校区（PATCH） */
    update: (id: string) => `/campuses/${id}`,
    /** 停用校区（POST） */
    suspend: (id: string) => `/campuses/${id}/suspend`,
    /** 启用校区（POST） */
    activate: (id: string) => `/campuses/${id}/activate`,
  },
  subjects: {
    /** 科目列表（GET，含筛选与分页） */
    list: "/subjects",
    /** 科目详情（GET） */
    detail: (id: string) => `/subjects/${id}`,
    /** 新建科目（POST） */
    create: "/subjects",
    /** 编辑科目（PATCH，code 不可改） */
    update: (id: string) => `/subjects/${id}`,
    /** 停用科目（POST） */
    disable: (id: string) => `/subjects/${id}/disable`,
    /** 启用科目（POST） */
    enable: (id: string) => `/subjects/${id}/enable`,
  },
  classrooms: {
    /** 教室列表（GET，含筛选与分页） */
    list: "/classrooms",
    /** 教室详情（GET） */
    detail: (id: string) => `/classrooms/${id}`,
    /** 新建教室（POST） */
    create: "/classrooms",
    /** 编辑教室（PATCH） */
    update: (id: string) => `/classrooms/${id}`,
    /** 停用教室（POST） */
    disable: (id: string) => `/classrooms/${id}/disable`,
    /** 启用教室（POST） */
    enable: (id: string) => `/classrooms/${id}/enable`,
  },
  scheduling: {
    /** 发布排课（POST，多星期批量 + 冲突预检 + 课次生成） */
    publish: "/schedules",
    /** 班级排课规则列表（GET，含未来课次派生计数） */
    listSchedules: "/schedules",
    /** 发布通知对象预览（GET，在班学生数 + 已关注监护人数，5-5 ADM-19） */
    notificationPreview: "/schedules/notification-preview",
    /** 结束排课（DELETE，携带二次确认标记，取消未来课次） */
    end: (id: string) => `/schedules/${id}`,
    /** 周历课次列表（GET，日期区间） */
    lessonSessions: "/lesson-sessions",
    /** 课次详情（GET，派生字段 + 学生摘要） */
    lessonSessionDetail: (id: string) => `/lesson-sessions/${id}`,
    /** 课次实例调整（POST，教室/日期/开始时间） */
    adjustLessonSession: (id: string) => `/lesson-sessions/${id}/adjustment`,
    /** 取消单课次（POST，携带二次确认标记，仅待上课且未消课课次） */
    cancelLessonSession: (id: string) => `/lesson-sessions/${id}/cancellation`,
  },
  classes: {
    /** 班级列表（GET，含筛选与分页 + 派生容量） */
    list: "/classes",
    /** 按学生查班级（GET，含 ENDED 历史班级，学生详情浮层班级 Tab） */
    byStudent: (studentId: string) => `/classes/by-student/${studentId}`,
    /** 班级详情（GET） */
    detail: (id: string) => `/classes/${id}`,
    /** 新建班级（POST，DRAFT 初始） */
    create: "/classes",
    /** 编辑班级（PATCH，乐观锁；校区/SKU 不可改） */
    update: (id: string) => `/classes/${id}`,
    /** 结课（POST，二次确认 -> COMPLETED） */
    complete: (id: string) => `/classes/${id}/complete`,
    /** 删除班级（DELETE，软删除 -> ARCHIVED，仅授权角色） */
    remove: (id: string) => `/classes/${id}`,
    /** 成员名单（GET，按学生去重显示最终状态） */
    members: (id: string) => `/classes/${id}/members`,
    /** 添加成员（POST，四步校验链） */
    enrollMember: (id: string) => `/classes/${id}/members`,
    /** 移除成员（DELETE，软删除 ENDED） */
    removeMember: (id: string, studentId: string) =>
      `/classes/${id}/members/${studentId}`,
  },
  placements: {
    /** 分班候选池 + 分组建议（GET，五维筛选，纯计算不持久化） */
    suggestions: "/placements/suggestions",
    /** 添加候选联想搜索（GET，科目剩余课时口径） */
    studentsSearch: "/placements/students/search",
    /** 确认预览（POST，自动生成建班四要素，只读不持久化） */
    preview: "/placements/preview",
    /** 最终确认（POST，RECRUITING 建班 + 批量入班，部分成功返回逐生明细） */
    confirm: "/placements/confirm",
  },
  courseSkus: {
    /** 课程 SKU 列表（GET，含筛选与分页） */
    list: "/course-skus",
    /** 课程 SKU 详情（GET） */
    detail: (id: string) => `/course-skus/${id}`,
    /** 新建课程 SKU（POST，初始 DRAFT） */
    create: "/course-skus",
    /** 编辑课程 SKU（PATCH，已上架不可编辑） */
    update: (id: string) => `/course-skus/${id}`,
    /** 上架课程 SKU（POST） */
    publish: (id: string) => `/course-skus/${id}/publish`,
    /** 下架课程 SKU（POST） */
    unpublish: (id: string) => `/course-skus/${id}/unpublish`,
    /** 停用课程 SKU（POST，校验在售商品引用） */
    disable: (id: string) => `/course-skus/${id}/disable`,
  },
  products: {
    /** 商品列表（GET，含名称/省市/状态/包含 SKU 筛选与分页） */
    list: "/products",
    /** 商品详情（GET，含 SKU 配置与派生字段） */
    detail: (id: string) => `/products/${id}`,
    /** 新建课程商品（POST，初始 DRAFT，随附 SKU 配置） */
    create: "/products",
    /** 编辑课程商品（PATCH，已上架不可编辑，SKU 配置整体覆盖） */
    update: (id: string) => `/products/${id}`,
    /** 上架课程商品（POST，二次确认） */
    publish: (id: string) => `/products/${id}/publish`,
    /** 下架课程商品（POST，二次确认） */
    unpublish: (id: string) => `/products/${id}/unpublish`,
    /** 删除课程商品（DELETE，仅未被订单引用且非上架中） */
    remove: (id: string) => `/products/${id}`,
    /** 签发封面上传凭证（POST，product-covers/ 前缀） */
    coverUploadCredentials: "/products/cover-upload-credentials",
    /** 商品销售历史（GET，只读 CQRS，一期空态） */
    salesHistory: (id: string) => `/products/${id}/sales-history`,
  },
  enrollments: {
    /** 报名提交（POST，员工/管理端三入口共用接口；响应 submission+applications 组结构） */
    create: "/enrollments",
    /** 报名列表（GET，单条口径 + 归属组/来源商品 + 转化派生；员工端强制当前校区） */
    list: "/enrollments",
    /** 报名详情（GET，单条兼容路径 + 归属字段） */
    detail: (id: string) => `/enrollments/${id}`,
    /** 编辑安全字段（PATCH，代理到组编辑整组生效，响应 {submissionId}） */
    update: (id: string) => `/enrollments/${id}`,
    /** 转订单候选（GET，兼容路径：内部解析所属组返回组级三类差异） */
    convertCandidates: (id: string) => `/enrollments/${id}/convert-candidates`,
  },
  orders: {
    /** 创建订单（POST，从报名转订单 / 复购直建） */
    create: "/orders",
    /** 管理端直建订单（POST） */
    createDirect: "/orders/direct",
    /** 订单列表（GET，筛选 + 分页 + 派生款项字段） */
    list: "/orders",
    /** 按当前筛选导出订单 CSV（GET，附件下载，手机号脱敏） */
    export: "/orders/export",
    /** 订单详情（GET，含报名覆盖与审计信息） */
    detail: (id: string) => `/orders/${id}`,
    /** 取消待支付订单（POST） */
    cancel: (id: string) => `/orders/${id}/cancel`,
    /** 登记订单退款（POST，ADM-09 REFUND 权限，幂等键防重） */
    refunds: (orderId: string) => `/orders/${orderId}/refunds`,
    /** 签发退款凭证短时上传凭证（POST，ADM-09 REFUND 权限） */
    refundEvidenceUpload: (orderId: string) =>
      `/orders/${orderId}/refund-evidences/upload-credentials`,
    /** 查询线下收款登记上下文（GET） */
    offlinePaymentContext: (orderId: string) =>
      `/orders/${orderId}/offline-payment-context`,
    /** 登记线下收款（POST，需 Idempotency-Key） */
    recordOfflinePayment: (orderId: string) =>
      `/orders/${orderId}/offline-payments`,
    /** 签发线下回单短时上传凭证（POST） */
    offlinePaymentReceiptUploadCredentials: (orderId: string) =>
      `/orders/${orderId}/offline-payment-receipts/upload-credentials`,
    /** 服务端中转访问线下回单（POST） */
    offlinePaymentReceiptAccess: (orderId: string) =>
      `/orders/${orderId}/offline-payment-receipts/access`,
    /** 查询当前节点催缴收件人（GET，家长微信号 × 公众号关注状态） */
    dunningRecipients: (orderId: string, planId: string) =>
      `/orders/${orderId}/receivable-plans/${planId}/dunning/recipients`,
    /** 对指定微信号发送催缴（POST） */
    sendDunning: (orderId: string, planId: string) =>
      `/orders/${orderId}/receivable-plans/${planId}/dunning`,
    /** 家长软删除已取消订单（DELETE） */
    softDelete: (id: string) => `/orders/${id}`,
  },
  revenueStatistics: {
    /** 收入统计汇总（GET，五指标 + 筛选回显） */
    summary: "/revenue-statistics",
    /** 收入统计 SKU 明细（GET，无分页） */
    skuBreakdown: "/revenue-statistics/sku-breakdown",
  },
  operationsDashboard: {
    /** 运营首页（GET，九类指标 + 今日课次 + 待办摘要；query: campusId/statDate） */
    summary: "/operations/dashboard",
  },
  paymentEntries: {
    /** 生成收款入口（POST，QR/LINK；allowDeposit 默认 false；同订单同应收节点同类型幂等返回既有入口） */
    create: "/payment-entries",
    /** 按订单查入口列表（GET，管理端对话框 / 员工端查看；query: orderId） */
    list: "/payment-entries",
    /** 入口详情与实时派生态（GET） */
    detail: (id: string) => `/payment-entries/${id}`,
    /** 修改分期授权（PATCH，仅首笔款项入口生成后支付前；尾款恒 false 不可改） */
    updateAllowDeposit: (id: string) => `/payment-entries/${id}/allow-deposit`,
    /** 转发审计（POST，员工「发送微信给家长」点击记审计） */
    forwardAudit: (id: string) => `/payment-entries/${id}/forward-audit`,
    /** 模拟家长已支付（POST，仅 mock 通道；真通道下 404） */
    mockPaid: (id: string) => `/payment-entries/${id}/mock-paid`,
  },
  reconciliationRuns: {
    /** 手动补跑指定账单日对账（POST，幂等返回既有运行或新执行结果） */
    create: "/reconciliation-runs",
    /** 对账运行记录列表（GET，账单日/状态/触发来源筛选 + 分页） */
    list: "/reconciliation-runs",
  },
  fundExceptions: {
    /** 资金差错单列表（GET，六维筛选 + 分页） */
    list: "/fund-exceptions",
    /** 差错单详情（GET，双侧证据 + 处理轨迹） */
    detail: (id: string) => `/fund-exceptions/${id}`,
    /** 差错单六动作统一处理（POST，幂等键随请求体提交） */
    resolve: (id: string) => `/fund-exceptions/${id}/resolve`,
  },
  fundsAlerts: {
    /** 资金健康告警列表（GET，指标/状态/触发时间筛选 + 分页） */
    list: "/funds-alerts",
  },
  makeupDeductions: {
    /** 补消记录列表（GET，固定 batchType=MAKEUP + 筛选分页） */
    list: "/makeup-deductions",
    /** 新增补消（POST，必填中文原因 ≤255） */
    submit: "/makeup-deductions",
    /** 补消名单评估（GET，query: sessionId，逐生消课状态与可勾选标记） */
    roster: "/makeup-deductions/roster",
    /** 补消名单手动加入（POST，O-22：ADM-33 ADD_MEMBER，原因必填） */
    rosterAdditions: "/makeup-deductions/roster/additions",
    /** 补消合格课次分页（GET，过去已结束 SCHEDULED/COMPLETED 课次） */
    eligibleSessions: "/lesson-sessions/makeup-eligible",
  },
  manualTrialGrants: {
    /** 手动赠送试课（POST，ADM-02 GRANT 权限；幂等键重放返回 applied=false + 当前余额） */
    create: "/manual-trial-grants",
    /** 赠课记录下钻明细（GET，账本 MANUAL_GRANT 流水定位） */
    detail: (id: string) => `/manual-trial-grants/${id}`,
  },
  deductionBatches: {
    /** 消课批次列表（GET，筛选 + 分页） */
    list: "/deduction-batches",
    /** 消课批次详情（GET，含学生结果明细与流水号） */
    detail: (id: string) => `/deduction-batches/${id}`,
  },
  deductions: {
    /** 消课明细列表（GET，ADM-23 双区块数据源：result 多值 + 姓名筛选） */
    list: "/deductions",
    /** 消课明细详情（GET，异常详情抽屉） */
    detail: (id: string) => `/deductions/${id}`,
    /** 发起冲正（POST，必填中文 reason + 客户端幂等键 UUID） */
    reversal: (id: string) => `/deductions/${id}/reversal`,
  },
  entitlementLedger: {
    /** 全机构账本流水（GET，默认全量倒序，学生/账户/类型筛选） */
    entries: "/entitlement-ledger/entries",
    /** 流水详情（GET，含两池前后值/幂等键/冲正原因） */
    entryDetail: (entryId: string) => `/entitlement-ledger/entries/${entryId}`,
    /** 学生账户摘要（GET，逐 SKU 余额与状态） */
    studentAccounts: (studentId: string) =>
      `/entitlement-ledger/students/${studentId}/accounts`,
    /** 账户对账单（GET，getStatement 接线，含守恒标记） */
    statement: (accountId: string) =>
      `/entitlement-ledger/accounts/${accountId}/statement`,
  },
  periodPasses: {
    /** 调整期限卡开始日期（PATCH，ADM-24:PERIOD_ADJUST 动作；409=已到期/乐观锁冲突） */
    adjustStartDate: (id: string) => `/period-passes/${id}/start-date`,
    /** 学生期限卡列表（GET，ADM-24 卡片区数据源，按购买先后排序） */
    byStudent: (studentId: string) => `/students/${studentId}/period-passes`,
    /** 期限卡详情（GET，业务编号 PERIOD_PASS 下钻只读摘要；404 回退说明抽屉） */
    detail: (id: string) => `/period-passes/${id}`,
  },
  entitlementBalances: {
    /** 学生×SKU 课时余额列表（GET，姓名/SKU 模糊筛选，姓名+SKU 排序，三列口径） */
    list: "/entitlement-balances",
    /** 学生×SKU 课时流水（GET，点击课时数字弹窗数据源，全账户全类型倒序） */
    history: "/entitlement-balances/history",
  },
  enrollmentSubmissions: {
    /** 提交组列表（GET，组级聚合 + 来源商品维度筛选 + n/m 已购派生） */
    list: "/enrollment-submissions",
    /** 提交组详情（GET，双层：组 + 组内子报名 + 快照摘要） */
    detail: (id: string) => `/enrollment-submissions/${id}`,
    /** 组级编辑安全字段（PATCH，一次作用整组） */
    update: (id: string) => `/enrollment-submissions/${id}`,
    /** 组级转订单候选（GET，三类差异 + 默认选中来源商品） */
    convertCandidates: (id: string) =>
      `/enrollment-submissions/${id}/convert-candidates`,
  },
  students: {
    /** 学生列表（GET，筛选 + 分页 + 脱敏） */
    list: "/students",
    /** 学生详情（GET，脱敏） */
    detail: (id: string) => `/students/${id}`,
    /** 新建学生（POST，生成学号） */
    create: "/students",
    /** 编辑学生（PATCH，只读保护 + 手机号变更事件） */
    update: (id: string) => `/students/${id}`,
    /** 归档学生（POST，幂等） */
    archive: (id: string) => `/students/${id}/archive`,
    /** 恢复学生（POST，幂等） */
    restore: (id: string) => `/students/${id}/restore`,
    /** CSV 导入预览（POST，不落库） */
    importPreview: "/students/import/preview",
    /** CSV 确认导入（POST，幂等） */
    importConfirm: "/students/import",
    /** 学生联想（GET，姓名/手机号脱敏） */
    search: "/students/search",
    /** 明文查看手机号（GET，权限 + 审计） */
    mobilePlaintext: (id: string) => `/students/${id}/mobile`,
    /** 按手机号查学生（GET，内部端口透出） */
    byMobile: "/students/by-mobile",
  },
  operationLogs: {
    /** 操作日志分页查询（GET，9 维筛选 + 数据范围 + operated_at 倒序） */
    list: "/operation-logs",
    /** 操作日志详情（GET，前后值/业务原因/detail 结构化明细） */
    detail: (id: string) => `/operation-logs/${id}`,
  },
  exports: {
    /** 创建导出任务（POST，≤10000 同步完成 / 超限排队） */
    create: "/exports",
    /** 导出任务状态（GET） */
    status: (id: string) => `/exports/${id}`,
    /** 导出文件下载（GET，短时有效，逐次审计） */
    download: (id: string) => `/exports/${id}/download`,
  },
} as const;
